import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Theme } from '@app-types/theme.type';
import { UserSubscription } from '@app-types/subscription.type';
import { UpdateUserBasicInfo } from '@app-types/update-user-basic-info.type';
import { Invoice } from '@app-types/invoice.type';
import { User } from '@app-types/user.type';
import ResponseCodes from '@constants/response.codes';
import SubscriptionStatus from '@constants/subscription.status';
import Header from '@components/header/header.component';
import Separator from '@components/separator/separator.component';
import UserInvoicesDetails from '@components/user-invoices-details/user-invoices-details.component';
import {
    TNController,
    ToastNotification,
} from '@components/toast-notification/toast-notification.component';
import GoBackView from '@components/go-back-view/go-back-view';
import View from '@components/view/view';
import WarningModal from '@components/warning-modal/warning-modal.component';
import { removeEmptyFields } from '@utils/entity.util';
import { subscriptionSdk } from '@services/subscription.service';
import { subscriptionsSDK } from '@services/subscriptions';
import { userSdk } from '@services/user.service';
import { invoiceSdk } from '@services/invoice.service';
import { useAuthStore } from '@stores/auth-store';

import Vehicles from './vehicles';
import AddVehicleModal from './add-vehicle-modal';
import UserSubscriptions from './containers/user-subscriptions';
import CancelSubscriptionModal from './components/cancel-subscription-modal';
import UpdateSubscriptionModal from './components/update-subscription-modal';
import UserBasicInfo from './containers/user-basic-info';
import styles from './user.module.scss';
import { SubscriptionAction } from './components/subscription-details-popover';

interface Props {
    theme?: Theme;
}

const UserView = (props: Props) => {
    const { ['id']: userId } = useParams();
    const { permissions } = useAuthStore();

    const [user, setUser] = useState<User | undefined>();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [vehicleModalShown, setVehicleModalShown] = useState(false);
    const [updatingBasicInfo, setUpdatingBasicInfo] = useState(false);
    const [cancelSubscriptionId, setCancelSubscriptionId] = useState<
        number | null
    >(null);
    const [pauseSubscription, setPauseSubscription] = useState({
        modalShown: false,
        subscriptionId: 0,
        confirmLoading: false,
        isResume: false,
    });
    const [updateSubscriptionId, setUpdateSubscriptionId] = useState<
        number | null
    >(null);
    const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);

    useEffect(() => {
        fetchUser(Number(userId));
        fetchInvoices(Number(userId));
        fetchSubscriptions(Number(userId));
    }, [userId]);

    const fetchSubscriptions: (userId: number) => Promise<void> = useCallback(
        async (userId: number) => {
            const subscriptions =
                await subscriptionsSDK.admin.getUserSubscriptions(userId);
            if (!subscriptions) return;
            setSubscriptions(subscriptions);
        },
        [setSubscriptions]
    );

    const fetchInvoices = async (userId: number) => {
        const response = await invoiceSdk.getAll({
            payerId: userId,
            page: 1,
            count: 20,
        });
        if (response.code !== ResponseCodes.PROCESS_OK) {
            console.error('[!] Error getting invoices of user:', response);
        }
        setInvoices(response.data.result);
    };

    const fetchUser = useCallback(async (id: number) => {
        const response = await userSdk.getById(id, ['vehicles']);
        if (response.code !== ResponseCodes.PROCESS_OK) {
            return console.error('[!] Error getting user:', response);
        }
        const userModel = response.data as User;
        setUser(userModel);
    }, []);

    const updateUserBasicInfo = async (update: UpdateUserBasicInfo) => {
        setUpdatingBasicInfo(true);
        const response = await userSdk.update(
            Number(userId),
            removeEmptyFields(update)
        );
        setUpdatingBasicInfo(false);
        if (response.code !== ResponseCodes.PROCESS_OK) {
            return TNController.show(response.message, 2500, 'error');
        }

        TNController.show('Usuario actualizado exitosamente', 2500);
    };

    const onVehicleCreated = () => {
        setVehicleModalShown(false);
        fetchUser(Number(userId));
    };

    const onPauseResume = async () => {
        const {
            confirmLoading,
            subscriptionId: id,
            isResume,
        } = pauseSubscription;
        if (confirmLoading || id === 0) return;

        setPauseSubscription((prev) => ({ ...prev, confirmLoading: true }));

        const result = isResume
            ? await subscriptionSdk.resumeSubscription(id)
            : await subscriptionSdk.pauseSubscription(id);

        if ('error' in result) {
            setPauseSubscription((prev) => ({
                ...prev,
                confirmLoading: false,
            }));
            return TNController.show(result.error, 2500, 'error');
        }

        setPauseSubscription({
            modalShown: false,
            subscriptionId: 0,
            confirmLoading: false,
            isResume: false,
        });
        fetchSubscriptions(Number(userId));
        fetchUser(Number(userId));
        TNController.show(
            isResume ? 'Suscripción reanudada' : 'Suscripción pausada',
            2500
        );
    };

    const handleSubscriptionAction = (
        subscriptionId: number,
        action: SubscriptionAction
    ) => {
        switch (action) {
            case SubscriptionAction.SUSPEND: {
                const sub = subscriptions.find((s) => s.id === subscriptionId);
                const isResume = sub?.status === SubscriptionStatus.SUSPENDED;
                return setPauseSubscription({
                    modalShown: true,
                    subscriptionId,
                    confirmLoading: false,
                    isResume,
                });
            }

            case SubscriptionAction.UPDATE_DATA:
                return setUpdateSubscriptionId(subscriptionId);

            case SubscriptionAction.INACTIVATE:
            default:
                return setCancelSubscriptionId(subscriptionId);
        }
    };

    const handleUpdateSubscriptionDone = useCallback(() => {
        setUpdateSubscriptionId(null);
        fetchSubscriptions(Number(userId));
        fetchUser(Number(userId));
    }, [fetchSubscriptions, fetchUser, userId]);

    return (
        <View permissions={permissions} requiredPermission="read-users">
            <Header
                mode={props.theme}
                title={`Usuario: ${user?.firstName + ' ' + user?.lastName}`}
                subtitle={'Repasemos el detalle de este usuario'}
            />
            <div className={styles.container}>
                <GoBackView />
                {user && (
                    <UserBasicInfo
                        user={user}
                        onUpdate={updateUserBasicInfo}
                        updating={updatingBasicInfo}
                    />
                )}
                <Separator />
                <div className={styles.details}>
                    <Vehicles
                        vehicles={user?.vehicles}
                        onAdd={() => setVehicleModalShown(true)}
                        onUpdate={() => fetchUser(Number(userId))}
                    />
                    <UserSubscriptions
                        subscriptions={subscriptions}
                        onAction={handleSubscriptionAction}
                    />
                    <UserInvoicesDetails
                        theme={props.theme}
                        invoices={invoices}
                    />
                </div>
            </div>
            {vehicleModalShown && (
                <AddVehicleModal
                    userId={Number(userId)}
                    onConfirm={onVehicleCreated}
                    onDiscard={() => setVehicleModalShown(false)}
                />
            )}
            {cancelSubscriptionId !== null && (
                <CancelSubscriptionModal
                    subscriptionId={cancelSubscriptionId}
                    onDone={() => {
                        setCancelSubscriptionId(null);
                        fetchSubscriptions(Number(userId));
                    }}
                />
            )}
            {updateSubscriptionId !== null && (
                <UpdateSubscriptionModal
                    subscriptionId={updateSubscriptionId}
                    onDone={handleUpdateSubscriptionDone}
                />
            )}
            {pauseSubscription.modalShown && (
                <WarningModal
                    title={
                        pauseSubscription.isResume
                            ? 'Reanudar suscripción'
                            : 'Pausar suscripción'
                    }
                    body={
                        pauseSubscription.isResume
                            ? 'La suscripción volverá a estar activa y el usuario podrá usar los servicios nuevamente. ¿Estás seguro de querer continuar?'
                            : 'Si confirmas esta acción la suscripción se pausará. Mientras esté pausada, el usuario no podrá usar los servicios. Podrás reanudarla en cualquier momento. ¿Estás seguro de querer continuar?'
                    }
                    confirmText={
                        pauseSubscription.isResume
                            ? 'Sí, reanudar'
                            : 'Sí, pausar'
                    }
                    discardText="No, volver"
                    confirmLoading={pauseSubscription.confirmLoading}
                    onConfirm={onPauseResume}
                    onDiscard={() =>
                        setPauseSubscription({
                            modalShown: false,
                            subscriptionId: 0,
                            confirmLoading: false,
                            isResume: false,
                        })
                    }
                />
            )}
            <ToastNotification />
        </View>
    );
};

export default UserView;
